/**
 * usePlaid.js — bank feed hooks (Plaid)
 *
 * Backend paths are '/plaid/...' — api.js prepends '/api'.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

const qs = (params) => {
  const s = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return s ? `?${s}` : '';
};

// Anything that changes review state moves numbers on every other panel, so
// they all invalidate together rather than each caller remembering the set.
const BANK_KEYS = [
  ['bank_status'], ['bank_accounts'], ['bank_transactions'],
  ['bank_spend'], ['bank_runway'], ['bank_by_job'], ['expenses'],
];
function invalidateBank(qc) {
  BANK_KEYS.forEach((key) => qc.invalidateQueries({ queryKey: key }));
}

// ── Status + accounts ────────────────────────────────────────

export function useBankStatus() {
  return useQuery({
    queryKey: ['bank_status'],
    queryFn: () => api.get('/plaid/status'),
    staleTime: 60 * 1000,
  });
}

export function useBankAccounts(enabled = true) {
  return useQuery({
    queryKey: ['bank_accounts'],
    queryFn: () => api.get('/plaid/accounts'),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/plaid/accounts/${id}`, body),
    onSuccess: () => invalidateBank(qc),
  });
}

// ── Connect flow ─────────────────────────────────────────────

/** Fetch a link_token on demand — they expire, so never cache one. */
export function useCreateLinkToken() {
  return useMutation({
    mutationFn: (connectionId) =>
      api.post(connectionId ? `/plaid/link-token/${connectionId}` : '/plaid/link-token'),
  });
}

export function useExchangePublicToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicToken) => api.post('/plaid/exchange', { publicToken }),
    onSuccess: () => invalidateBank(qc),
  });
}

export function useDisconnectBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId) => api.del(`/plaid/connections/${connectionId}`),
    onSuccess: () => invalidateBank(qc),
  });
}

export function useSyncBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId) =>
      api.post(connectionId ? `/plaid/sync/${connectionId}` : '/plaid/sync'),
    onSuccess: () => invalidateBank(qc),
  });
}

// ── Transactions ─────────────────────────────────────────────

export function useBankTransactions(filters = {}, enabled = true) {
  return useQuery({
    queryKey: ['bank_transactions', filters],
    queryFn: () => api.get(`/plaid/transactions${qs(filters)}`),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useUpdateBankTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/plaid/transactions/${id}`, body),
    onSuccess: () => invalidateBank(qc),
  });
}

/** Create the linked `expenses` row. The only path from bank data to the P&L. */
export function usePromoteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.post(`/plaid/transactions/${id}/promote`, body),
    onSuccess: () => invalidateBank(qc),
  });
}

export function useIgnoreTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }) => api.post(`/plaid/transactions/${id}/ignore`, { notes }),
    onSuccess: () => invalidateBank(qc),
  });
}

/** Undo a review decision; deletes the promoted expense if there was one. */
export function useUnreviewTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/plaid/transactions/${id}/unreview`),
    onSuccess: () => invalidateBank(qc),
  });
}

// ── Analytics ────────────────────────────────────────────────

export function useBankSpend(months = 6, enabled = true) {
  return useQuery({
    queryKey: ['bank_spend', months],
    queryFn: () => api.get(`/plaid/analytics/spend?months=${months}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBankRunway(months = 3, enabled = true) {
  return useQuery({
    queryKey: ['bank_runway', months],
    queryFn: () => api.get(`/plaid/analytics/runway?months=${months}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBankSpendByJob(enabled = true) {
  return useQuery({
    queryKey: ['bank_by_job'],
    queryFn: () => api.get('/plaid/analytics/by-job'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
